import { RandomId } from "@swivl-mono/helpers";
import {
  MemberInterface,
  NodeData_Ticket,
  NodeInterface,
  NodeType,
  TicketNodeMode,
  TicketStatus,
  TicketTemplate,
  orderedTicketPriorities,
  ticketPriorityLabels
} from "@swivl-mono/types";
import { Input, Select, SelectItem } from "@swivl-mono/ui";
import { useEffect, useState } from "react";

import { TagInput, TagInputType } from "../../../../../Components/TagInput/TagInput";
import MembersModel from "../../../../../Models/Member/Members.Model";
import { useTicketingStore } from "../../../../../Models/Ticketing/Ticketing.Store";
import TabNodeLayoutData from "../../NodeLayouts/TabNodeLayoutData";
import InspectorFooter from "../../SidePanel/Inspector/components/InspectorFooter";
import InspectorNodeHeader from "../../SidePanel/Inspector/components/InspectorNodeHeader";
import NodePanel, { NodePanelProps } from "../NodePanel";

const generateSlug = () => RandomId(4);

const TicketNode = {
  Type: NodeType.Ticket,

  Empty: function (): NodeData_Ticket {
    return {
      id: RandomId(6),
      type: NodeType.Ticket,
      mode: TicketNodeMode.CREATE,
      nodeSlug: generateSlug(),
      title: "New Ticket",
      goto: ""
    };
  },

  PlumbData: function (node: NodeInterface) {
    const ticketNode = node as NodeData_Ticket;
    const modeLabels: Record<TicketNodeMode, string> = {
      [TicketNodeMode.CREATE]: "Create",
      [TicketNodeMode.CREATE_OR_UPDATE]: "Create/Update",
      [TicketNodeMode.UPDATE_ONLY]: "Update"
    };
    const modeLabel = modeLabels[ticketNode.mode] || "Ticket";

    // Different output tabs based on mode
    let tabs;
    switch (ticketNode.mode) {
      case TicketNodeMode.CREATE_OR_UPDATE:
        tabs = [
          { id: "gotoCreated", source: "gotoCreated", target: ticketNode.gotoCreated, title: "Created" },
          { id: "gotoUpdated", source: "gotoUpdated", target: ticketNode.gotoUpdated, title: "Updated" }
        ];
        break;
      case TicketNodeMode.UPDATE_ONLY:
        tabs = [
          { id: "gotoUpdated", source: "gotoUpdated", target: ticketNode.gotoUpdated, title: "Updated" },
          { id: "gotoNotFound", source: "gotoNotFound", target: ticketNode.gotoNotFound, title: "Not Found" }
        ];
        break;
      case TicketNodeMode.CREATE:
      default:
        tabs = [{ id: "goto", source: "goto", target: ticketNode.goto }];
        break;
    }

    return TabNodeLayoutData({
      node: node,
      label: "Ticket",
      title: `${modeLabel}: ${ticketNode.title || "Untitled"}`,
      baseWidth: 200,
      tabs
    });
  },

  InspectorPanel: function TicketInspectorPanelWrapper(props: NodePanelProps) {
    const { templates, tagOptions, loadTemplates, ensureTagsLoaded } = useTicketingStore();
    const [members, setMembers] = useState<MemberInterface[]>([]);

    useEffect(() => {
      loadTemplates();
      ensureTagsLoaded();
      MembersModel.loadMembers().then(() => {
        setMembers(MembersModel.state.members ?? []);
      });
    }, [loadTemplates, ensureTagsLoaded]);

    return <TicketNode.Internal_InspectorPanel {...props} templates={templates} tags={tagOptions} members={members} />;
  },

  Internal_InspectorPanel: class extends NodePanel {
    render() {
      const node = this.props.node as NodeData_Ticket;
      const { templates, tags, members } = this.props as any;

      const modeOptions: SelectItem[] = [
        { value: TicketNodeMode.CREATE, label: "Create" },
        // { value: TicketNodeMode.CREATE_OR_UPDATE, label: "Create or Update" },
        { value: TicketNodeMode.UPDATE_ONLY, label: "Update Only" }
      ];

      const statusOptions: SelectItem[] = [
        { value: "", label: "-- No Change --" },
        { value: TicketStatus.TODO, label: "To Do" },
        { value: TicketStatus.IN_PROGRESS, label: "In Progress" },
        { value: TicketStatus.ON_HOLD, label: "On Hold" },
        { value: TicketStatus.COMPLETED, label: "Completed" },
        { value: TicketStatus.CANCELLED, label: "Cancelled" }
      ];

      const priorityOptions: SelectItem[] = [
        { value: "", label: "-- No Change --" },
        ...orderedTicketPriorities.map((p) => ({ value: p, label: ticketPriorityLabels[p] }))
      ];

      const templateOptions: SelectItem[] = [
        { value: "", label: "-- None --" },
        ...(templates || []).map((t: TicketTemplate) => ({ value: String(t.id), label: t.name }))
      ];

      const currentAssigneeId = node.assigneeMemberIds?.[0] ?? "";

      const memberOptions: SelectItem[] = [
        { value: "", label: "No Assignee" },
        ...(members || []).map((m: MemberInterface) => ({
          value: String(m.id),
          label: `${m.account?.firstName ?? ""} ${m.account?.lastName ?? ""}`.trim() || m.account?.email || `Member #${m.id}`
        }))
      ];

      const tagDropdownOptions: SelectItem[] = [{ value: "", label: "-- None --" }, ...(tags || []).map((t: string) => ({ value: t, label: t }))];

      const handleAssigneeChange = (e: any): void => {
        const value = e.target.value;
        const memberIds = value ? [parseInt(value, 10)] : [];
        this.onChange({ target: { name: "assigneeMemberIds", value: memberIds } });
      };

      // Handle template change - apply template's predefined fields
      const handleTemplateChange = (e: any) => {
        if (!this.props.canEdit) return;

        const templateId = e.target.value ? Number(e.target.value) : undefined;
        const selectedTemplate = templates?.find((t: TicketTemplate) => t.id === templateId);

        // Deep clone the current node (same pattern as other NodePanel methods)
        const updatedNode: NodeData_Ticket = JSON.parse(JSON.stringify(this.props.node));
        updatedNode.templateId = templateId;

        // Apply predefined fields from template definition
        // Template structure: { definition: { predefined: { title, priority, status, tags, assigneeMemberIds } } }
        const definition = selectedTemplate?.definition as any;
        const predefined = definition?.predefined;

        if (predefined) {
          if (predefined.title) {
            updatedNode.title = predefined.title;
          }
          if (predefined.priority) {
            updatedNode.priority = predefined.priority;
          }
          if (predefined.status) {
            updatedNode.status = predefined.status;
          }
          if (predefined.tags && predefined.tags.length > 0) {
            // Use first tag as tagToAdd (node only supports single tag)
            updatedNode.tagToAdd = predefined.tags[0];
          }
          if (predefined.assigneeMemberIds && predefined.assigneeMemberIds.length > 0) {
            // Filter to only include numeric IDs (ignore variable strings)
            const numericIds = predefined.assigneeMemberIds.filter((id: number | string) => typeof id === "number") as number[];
            if (numericIds.length > 0) {
              updatedNode.assigneeMemberIds = numericIds;
            }
          }
        }

        // Pass true to force redraw
        this.props.updateNode(updatedNode, this.props.node, true);
      };

      return (
        <div className="panel TicketNodePanel flex flex-col gap-2">
          <InspectorNodeHeader title="Ticket" />

          <Select
            label="Mode"
            options={modeOptions}
            selectedValues={[node.mode || ""]}
            hideClear
            onChange={(vals) => this.onChangeSwitch("mode", vals[0])}
          />

          <Input
            label="Node Slug"
            type="text"
            value={node.nodeSlug || ""}
            onChange={this.onChange}
            name="nodeSlug"
            helperText="Unique identifier for correlating tickets across interactions"
          />

          {(node.mode === TicketNodeMode.CREATE || node.mode === TicketNodeMode.CREATE_OR_UPDATE) && (
            <>
              <Select
                label="Template"
                options={templateOptions}
                selectedValues={[String(node.templateId || "")]}
                hideClear
                onChange={(vals) => handleTemplateChange({ target: { value: vals[0] } })}
              />
            </>
          )}

          {node.mode !== TicketNodeMode.UPDATE_ONLY && (
            <>
              <TagInput label="Title" value={node.title || ""} onChange={this.onChange} name="title" type={TagInputType.SingleLine} />

              <Select
                label="Assignee"
                options={memberOptions}
                selectedValues={[String(currentAssigneeId)]}
                hideClear
                isSearchable
                onChange={(vals) => handleAssigneeChange({ target: { value: vals[0] } })}
              />
            </>
          )}

          <TagInput label="Note" value={node.note || ""} onChange={this.onChange} name="note" placeholder="Optional note to add..." />

          <Select
            label="Status"
            options={statusOptions}
            selectedValues={[node.status || ""]}
            hideClear
            onChange={(vals) => this.onChangeSwitch("status", vals[0])}
          />

          <Select
            label="Priority"
            options={priorityOptions}
            selectedValues={[node.priority || ""]}
            hideClear
            onChange={(vals) => this.onChangeSwitch("priority", vals[0])}
          />

          <Select
            label="Add Tag"
            options={tagDropdownOptions}
            selectedValues={[node.tagToAdd || ""]}
            hideClear
            isSearchable
            onChange={(vals) => this.onChangeSwitch("tagToAdd", vals[0])}
          />

          <InspectorFooter
            deleteNode={this.deleteNode}
            node={this.props.node}
            duplicateNode={this.props.duplicateNode}
            canDuplicate={this.props.canDuplicate}
          />
        </div>
      );
    }
  }
};

export default TicketNode;
